import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountAuthService, SessionPayload } from '../account-auth.service';

type Mode = 'inbox' | 'verifying' | 'done' | 'failed';

const RESEND_WAIT_SECONDS = 60;

/**
 * /verify-email?email=…   after sign-up: "check your inbox", send again.
 * /verify-email?token=…   the link from the email: confirms and signs in.
 */
@Component({
  selector: 'app-verify-email-page',
  templateUrl: './verify-email-page.component.html',
  styleUrls: ['../auth-form.scss'],
  standalone: false,
})
export class VerifyEmailPageComponent implements OnInit, OnDestroy {
  mode: Mode = 'inbox';
  email = '';
  message = '';
  linkExpired = false;

  readonly emailControl = new FormControl('', [Validators.required, Validators.email]);
  resendBusy = false;
  resendSent = false;
  resendError: string | null = null;
  wait = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private auth: AccountAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.email = params.get('email') || '';
    this.emailControl.setValue(this.email);
    const token = params.get('token');
    if (token) {
      this.verify(token);
    } else if (params.get('sent')) {
      this.resendSent = true;
      this.startWait();
    } else {
      this.startWait(); // the sign-up just sent one
    }
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  private verify(token: string): void {
    this.mode = 'verifying';
    // Take the token out of the address bar (and browser history).
    this.router.navigate([], { queryParams: {}, replaceUrl: true });
    this.auth.verifyEmail(token).subscribe({
      next: (res) => {
        if ((res as SessionPayload).accesstoken) {
          this.auth.startSession(res as SessionPayload, { welcome: true }).catch(() => {
            this.router.navigate(['/login'], { queryParams: { reason: 'verified' } });
          });
          return;
        }
        this.mode = 'done';
        this.message = (res as { message: string }).message;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const error = this.auth.toError(err);
        this.mode = 'failed';
        this.linkExpired = error.code === 'link_expired';
        this.message = error.message;
        this.cdr.detectChanges();
      },
    });
  }

  resend(): void {
    if (this.resendBusy || this.wait > 0) return;
    const email = (this.mode === 'failed' ? this.emailControl.value : this.email) || '';
    if (this.mode === 'failed' && this.emailControl.invalid) {
      this.emailControl.markAsTouched();
      return;
    }
    this.resendBusy = true;
    this.resendError = null;
    this.auth.resendVerification(email.trim()).subscribe({
      next: () => {
        this.resendBusy = false;
        this.resendSent = true;
        if (this.mode === 'failed') {
          this.email = email.trim();
          this.mode = 'inbox';
        }
        this.startWait();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.resendBusy = false;
        this.resendError = this.auth.toError(err).message;
        this.cdr.detectChanges();
      },
    });
  }

  private startWait(): void {
    this.wait = RESEND_WAIT_SECONDS;
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => {
      this.wait = Math.max(0, this.wait - 1);
      if (this.wait === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.cdr.detectChanges();
    }, 1000);
  }
}
